// Hard-bounce status of the signed-in user's email (plan 101 N1). Reads
// `GET /v1/me/email-status` — whether the notification fanout has STOPPED mailing
// the user's address after a hard bounce (SMTP 5xx) — and asks for a retry with
// `POST /v1/me/email-status/retry` once the user says the address works again.
//
// Suppression is keyed on the ADDRESS server-side, so a user who changes their
// email clears it without touching this; retry is only for "same address, fixed".
// Per user, not per account, so the query key omits the active account (as
// `pushSubscriptions.ts` does).
//
// The wire is parsed, not cast: a `bouncing: true` with no address would render
// a notice about nothing, so that shape is an `ApiSchemaError` at the seam.

import { createQuery } from '@tanstack/solid-query';
import { z } from 'zod';
import { apiFetch } from './api';
import { SESSION_TOKEN } from './session';

const EmailStatusSchema = z.union([
  z.object({
    bouncing: z.literal(true),
    /** The address the fanout no longer mails. */
    address: z.string(),
    /** When it was suppressed (ISO-8601). */
    since: z.string(),
  }),
  z.object({ bouncing: z.literal(false) }),
]);

const EmailRetrySchema = z.object({
  /** True when a suppression was lifted; false when there was none to lift. */
  cleared: z.boolean(),
});

export type EmailStatus = z.infer<typeof EmailStatusSchema>;
export type EmailRetry = z.infer<typeof EmailRetrySchema>;

export const EMAIL_STATUS_QUERY_KEY = 'email_status';

/** The caller's email bounce status. Idle with no session, like `useNotificationPrefs`. */
export function useEmailStatus(token: string | null = SESSION_TOKEN) {
  return createQuery<EmailStatus>(() => ({
    queryKey: [EMAIL_STATUS_QUERY_KEY, token],
    enabled: token !== null,
    queryFn: async () =>
      apiFetch('/v1/me/email-status', token, undefined, { schema: EmailStatusSchema }),
  }));
}

/**
 * Lift the suppression so the next notification is mailed again. The server
 * audits it; if the address still bounces, the next 5xx suppresses it anew.
 */
export async function retryEmail(token: string | null = SESSION_TOKEN): Promise<EmailRetry> {
  return apiFetch(
    '/v1/me/email-status/retry',
    token,
    { method: 'POST' },
    {
      schema: EmailRetrySchema,
    },
  );
}
